<markdown-editor>
    <div class="markdown-editor-container"></div>

    <script>
        import Quill from 'quill';
        import 'quill/dist/quill.snow.css';
        import { marked } from 'marked';
        import MarkdownShortcuts from 'quilljs-markdown';

        // Register the markdown shortcuts module
        Quill.register('modules/markdownShortcuts', MarkdownShortcuts);

        export default {
            includeTools: false,

            state: {
                quillEditor: null,
                currentContent: ''
            },

            onBeforeMount() {
                // Set up default values for props
                this.state.currentContent = this.props.content || '';
            },

            onMounted() {
                this.initializeQuill();
            },

            onBeforeUnmount() {
                this.cleanup();
            },

            onUpdated(props, state) {
                // Re-initialize if readonly mode changes
                if (props.readonly !== undefined) {
                    this.initializeQuill();
                }

                // Update content if it changes externally
                if (props.content !== undefined && props.content !== this.state.currentContent) {
                    this.setContent(props.content);
                }
            },

            cleanup() {
                if (this.state.quillEditor) {
                    const container = this.$('.markdown-editor-container');
                    if (container) {
                        // Remove toolbar if exists
                        const existingToolbar = container.previousElementSibling;
                        if (existingToolbar && existingToolbar.classList.contains('ql-toolbar')) {
                            existingToolbar.remove();
                        }
                        container.innerHTML = '';
                    }
                    this.state.quillEditor = null;
                }
            },

            initializeQuill() {
                // Wait for DOM to be ready
                setTimeout(() => {
                    const container = this.$('.markdown-editor-container');
                    if (!container) return;

                    // Clear any existing editor
                    this.cleanup();

                    // Get props with defaults
                    const readonly = this.props.readonly !== undefined ? this.props.readonly : true;
                    const toolbarOptions = this.props.toolbarOptions || [
                        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                        ['bold', 'italic', 'underline'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }]
                    ];
                    const enableMarkdownShortcuts = this.props.enableMarkdownShortcuts !== undefined ? this.props.enableMarkdownShortcuts : true;
                    const placeholder = this.props.placeholder || '';

                    const modules = {
                        toolbar: readonly ? false : toolbarOptions
                    };

                    // Add markdown shortcuts module if enabled and in edit mode
                    if (!readonly && enableMarkdownShortcuts) {
                        modules.markdownShortcuts = {};
                    }

                    // Initialize Quill editor
                    const quill = new Quill(container, {
                        theme: 'snow',
                        readOnly: readonly,
                        placeholder: placeholder,
                        modules: modules
                    });

                    // Load content
                    if (this.state.currentContent) {
                        const html = marked.parse(this.state.currentContent, { breaks: true });
                        quill.clipboard.dangerouslyPasteHTML(html);
                    }

                    this.state.quillEditor = quill;

                    // Handle toolbar visibility
                    const toolbar = container.previousElementSibling;
                    if (toolbar && toolbar.classList.contains('ql-toolbar')) {
                        toolbar.style.display = readonly ? 'none' : 'block';
                    }

                    // Set up change listener
                    if (!readonly && this.props.onChange) {
                        quill.on('text-change', () => {
                            const markdown = this.getContent();
                            this.props.onChange(markdown);
                        });
                    }
                }, 0);
            },

            // Public method to get current markdown content
            getContent() {
                const quill = this.state.quillEditor;
                if (!quill) return this.state.currentContent;

                const html = quill.root.innerHTML;

                // Debug: Log the HTML to see what Quill is generating
                console.log('[Markdown Editor] HTML from Quill:', html);

                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = html;

                const markdown = this.convertHtmlToMarkdown(tempDiv);

                // Debug: Log the converted markdown
                console.log('[Markdown Editor] Converted Markdown:', markdown);

                return markdown;
            },

            // Public method to set content programmatically
            setContent(markdown) {
                this.state.currentContent = markdown;
                const quill = this.state.quillEditor;
                if (quill) {
                    const html = marked.parse(markdown, { breaks: true });
                    quill.clipboard.dangerouslyPasteHTML(html);
                }
            },

            // Public method to toggle readonly mode
            setReadonly(readonly) {
                // Note: This won't work as expected since props are passed from parent
                // The parent should update the readonly prop instead
                this.update({ readonly });
                this.initializeQuill();
            },

            // HTML to Markdown conversion
            convertHtmlToMarkdown(element) {
                const processNode = (node, listContext = null) => {
                    if (node.nodeType === Node.TEXT_NODE) {
                        return node.textContent;
                    }

                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const tagName = node.tagName.toLowerCase();

                        switch(tagName) {
                            case 'h1': {
                                const children = Array.from(node.childNodes).map(n => processNode(n)).join('');
                                return `# ${children}\n\n`;
                            }
                            case 'h2': {
                                const children = Array.from(node.childNodes).map(n => processNode(n)).join('');
                                return `## ${children}\n\n`;
                            }
                            case 'h3': {
                                const children = Array.from(node.childNodes).map(n => processNode(n)).join('');
                                return `### ${children}\n\n`;
                            }
                            case 'h4': {
                                const children = Array.from(node.childNodes).map(n => processNode(n)).join('');
                                return `#### ${children}\n\n`;
                            }
                            case 'h5': {
                                const children = Array.from(node.childNodes).map(n => processNode(n)).join('');
                                return `##### ${children}\n\n`;
                            }
                            case 'h6': {
                                const children = Array.from(node.childNodes).map(n => processNode(n)).join('');
                                return `###### ${children}\n\n`;
                            }
                            case 'p': {
                                const children = Array.from(node.childNodes).map(n => processNode(n)).join('');
                                return `${children}\n\n`;
                            }
                            case 'strong': {
                                const children = Array.from(node.childNodes).map(n => processNode(n)).join('');
                                return `**${children}**`;
                            }
                            case 'em': {
                                const children = Array.from(node.childNodes).map(n => processNode(n)).join('');
                                return `*${children}*`;
                            }
                            case 'u': {
                                const children = Array.from(node.childNodes).map(n => processNode(n)).join('');
                                return `_${children}_`;
                            }
                            case 'ol': {
                                // Quill uses <ol> for both ordered and bullet lists
                                // Check first <li> to determine list type via data-list attribute
                                const firstLi = node.querySelector('li');
                                const listType = firstLi?.getAttribute('data-list') || 'ordered';

                                if (listType === 'bullet') {
                                    // Bullet list
                                    return Array.from(node.children).map(li =>
                                        `- ${processNode(li, 'bullet').trim()}\n`
                                    ).join('') + '\n';
                                } else {
                                    // Ordered list
                                    return Array.from(node.children).map((li, i) =>
                                        `${i + 1}. ${processNode(li, 'ordered').trim()}\n`
                                    ).join('') + '\n';
                                }
                            }
                            case 'ul':
                                // Standard HTML bullet list
                                return Array.from(node.children).map(li =>
                                    `- ${processNode(li, 'bullet').trim()}\n`
                                ).join('') + '\n';
                            case 'li': {
                                // Skip the data-list attribute when processing children
                                const children = Array.from(node.childNodes).map(n => processNode(n, listContext)).join('');
                                return children;
                            }
                            case 'br':
                                return '\n';
                            default: {
                                const children = Array.from(node.childNodes).map(n => processNode(n, listContext)).join('');
                                return children;
                            }
                        }
                    }
                    return '';
                };

                const markdown = processNode(element);
                return markdown.trim();
            }
        }
    </script>

    <style>
        .markdown-editor-container {
            min-height: 200px;
        }

        /*
            Borderless by default. Every caller wraps the editor in its own
            framed container, so Quill's built-in border would just stack into
            a box-within-a-box. Callers that want a frame style the wrapper.
        */
        markdown-editor .ql-container.ql-snow,
        markdown-editor .ql-toolbar.ql-snow {
            border: none !important;
        }

        /* Ensure Quill styles are applied properly */
        .markdown-editor-container .ql-editor {
            min-height: 200px;
        }

        .markdown-editor-container .ql-editor.ql-blank::before {
            font-style: italic;
            color: #4b5563;
        }

        .markdown-editor-container h1,
        .markdown-editor-container h2,
        .markdown-editor-container h3 {
            margin: .8rem 0;
        }

        .markdown-editor-container ol {
            padding-left: 0;
        }
    </style>
</markdown-editor>
